// ============================================================================
// GST SOURCE DOCUMENT SERVICE (ADR-0025 §3)
// ============================================================================
// Receiving client files under a GST engagement. The server sniffs the format
// from the bytes, hashes them, stores them in object storage and records the
// row; this service only carries the file and what the uploader says it is.

import { get, post, downloadFile } from '../client/axiosClient';
import { gstEndpoints } from '../endpoints/gst.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { PaginatedResponse } from '../types';
import type {
  GstReceiveResult,
  GstSourceDocument,
  GstSourceDocumentSearchRequest,
} from '../types/gstEngagement.types';

export interface UploadGstSourceDocumentInput {
  file: File;
  /** One of GST_SOURCE_DOCUMENT_KINDS. The server validates. */
  declaredKind: string;
  notes?: string | null;
}

/**
 * What the upload endpoint returns: the row plus the server's message, which
 * says if the file is unreadable or was already on record. `alreadyReceived`
 * is read from the status: 201 means stored, 200 means nothing was written.
 */
export interface UploadGstSourceDocumentResult {
  document: GstSourceDocument;
  message: string;
  alreadyReceived: boolean;
}

class GstSourceDocumentService {
  async getAll(
    engagementId: string, params?: GstSourceDocumentSearchRequest,
  ): Promise<PaginatedResponse<GstSourceDocument>> {
    const endpoint = params
      ? buildEndpointWithQuery(gstEndpoints.engagementSourceDocumentsBasePath(engagementId), params as Record<string, any>)
      : gstEndpoints.engagementSourceDocuments(engagementId);

    const response = await get<any>(endpoint);
    const raw = response.data;
    const paged = raw?.data ?? {};

    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: paged.items ?? [],
      pagination: {
        currentPage: paged.page ?? 1,
        pageSize: paged.pageSize ?? 20,
        totalPages: paged.totalPages ?? 0,
        totalItems: paged.totalCount ?? 0,
        hasNextPage: paged.hasNext ?? false,
        hasPreviousPage: paged.hasPrevious ?? false,
      },
    };
  }

  async getById(engagementId: string, documentId: string): Promise<GstSourceDocument> {
    const response = await get<any>(gstEndpoints.engagementSourceDocumentById(engagementId, documentId));
    return response.data?.data as GstSourceDocument;
  }

  /**
   * Multipart upload. Built here rather than through the shared `uploadFile`
   * helper because that helper sends the file alone, and the declared kind is
   * the one thing only the uploader knows.
   */
  async upload(engagementId: string, input: UploadGstSourceDocumentInput): Promise<UploadGstSourceDocumentResult> {
    const form = new FormData();
    form.append('file', input.file, input.file.name);
    form.append('declaredKind', input.declaredKind);
    if (input.notes) form.append('notes', input.notes);

    const response = await post<any>(
      gstEndpoints.engagementSourceDocuments(engagementId),
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );

    return {
      document: response.data?.data as GstSourceDocument,
      message: response.data?.message ?? 'Received.',
      alreadyReceived: response.status === 200,
    };
  }

  /**
   * Receive a file as an expected file for a period (ADR-0025 R1.2). When the
   * file's layout is known the API parses and maps it in the same call; the
   * outcome says what happened or what a person must decide.
   */
  async receive(
    engagementId: string,
    input: {
      file: File; expectedFileId?: string | null; taxPeriodStart?: string | null; taxPeriodEnd?: string | null;
      declaredKind?: string | null; notes?: string | null; purpose?: 'RECEIPT' | 'SAMPLE';
    },
  ): Promise<{ result: GstReceiveResult; message: string }> {
    const form = new FormData();
    form.append('file', input.file, input.file.name);
    if (input.purpose) form.append('purpose', input.purpose);
    if (input.expectedFileId) form.append('expectedFileId', input.expectedFileId);
    if (input.taxPeriodStart) form.append('taxPeriodStart', input.taxPeriodStart);
    if (input.taxPeriodEnd) form.append('taxPeriodEnd', input.taxPeriodEnd);
    if (input.declaredKind) form.append('declaredKind', input.declaredKind);
    if (input.notes) form.append('notes', input.notes);

    const response = await post<any>(
      gstEndpoints.engagementSourceDocumentReceive(engagementId),
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return { result: response.data?.data as GstReceiveResult, message: response.data?.message ?? 'Received.' };
  }

  /** Streams the file as the client sent it, under the name the client gave it. */
  download(engagementId: string, doc: GstSourceDocument): Promise<void> {
    return downloadFile(gstEndpoints.engagementSourceDocumentDownload(engagementId, doc.id), doc.fileName);
  }
}

export const gstSourceDocumentService = new GstSourceDocumentService();
export default gstSourceDocumentService;
